// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  type TradeDetails,
  type TradeOrder,
  type TraderState,
  type TradeDetails as Trades,
} from '@shared/data-access/interfaces/redux/trades';
import { equityApi } from '@advisor-app/store/api/brokerage/equity-api';
import { proposalsApi } from '@shared/data-access/api/brokerage/proposals-api';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

export type TradeParams = Pick<
  TradeDetails,
  | 'ticker'
  | 'operation'
  | 'realized_capital_gains'
  | 'unrealized_capital_gains'
  | 'volume'
  | 'exchange'
  | 'columnName'
  | 'isin'
  | 'institution_id'
  | 'institution_name'
  | 'account_id'
  | 'from_iban'
  | 'to_iban'
  | 'inputType'
  | 'to_asset_details'
> & {
  contracts_number?: number;
  transfer_target?: string;
  to_isin?: string | null;
};
export type DeleteTradePayload = {
  index: number;
  ticker?: string;
  operation?: string;
};

export type UpdateTradeAccountsPayload = {
  index: number;
  from_iban?: string;
  to_iban?: string;
  institution_id?: string;
  institution_name?: string;
};
const initialState: TraderState = {
  trades: [],
  openTrades: [],
  loading: false,
};

export const traderSlice = createSlice({
  name: 'trader',
  initialState,
  reducers: {
    addUpdateTrades: (state, action: PayloadAction<Trades[]>) => {
      const { payload } = action;

      state.trades = state.trades
        .filter(
          trade =>
            typeof payload.find(t => {
              const sameTickerAndOperation =
                t.ticker === trade.ticker && t.operation === trade.operation;

              // For TRANSFER operations, also check transfer_target
              if (
                t.operation === OperationType.TRANSFER &&
                trade.operation === OperationType.TRANSFER
              ) {
                return (
                  sameTickerAndOperation &&
                  t.transfer_target === trade.transfer_target
                );
              }

              return sameTickerAndOperation;
            }) === 'undefined',
        )
        .concat(payload);
    },
    deleteTrade: (state, action: PayloadAction<DeleteTradePayload>) => {
      const { index } = action.payload;
      if (typeof index === 'number' && index >= 0) {
        state.trades.splice(index, 1);
      }
    },

    updateTradeAccounts: (
      state,
      action: PayloadAction<UpdateTradeAccountsPayload>,
    ) => {
      const { index, from_iban, to_iban, institution_id, institution_name } =
        action.payload;
      const trade = state.trades[index];
      if (!trade) {
        return;
      }

      if (from_iban !== undefined) {
        trade.from_iban = from_iban;
      }
      if (to_iban !== undefined) {
        trade.to_iban = to_iban;
      }
      if (institution_id !== undefined) {
        trade.institution_id = institution_id;
      }
      if (institution_name !== undefined) {
        trade.institution_name = institution_name;
      }
    },

    setTrades: (state, action: PayloadAction<Trades[]>) => {
      state.trades = action.payload;
    },

    resetTrades: state => {
      state.trades = [];
    },

    setOpenTrades: (state, action: PayloadAction<TradeOrder[]>) => {
      state.openTrades = action.payload;
    },

    resetOpenTrades: state => {
      state.openTrades = [];
    },
  },
  extraReducers: builder => {
    /* istanbul ignore next */
    builder
      .addMatcher(equityApi.endpoints.getOrderPrices.matchPending, state => {
        state.loading = true;
      })
      .addMatcher(
        equityApi.endpoints.getOrderPrices.matchFulfilled,
        (state, action) => {
          state.loading = false;

          traderSlice.caseReducers.addUpdateTrades(state, action);
        },
      )
      .addMatcher(equityApi.endpoints.getOrderPrices.matchRejected, state => {
        state.loading = false;
      })
      .addMatcher(
        equityApi.endpoints.getLatestPrices.matchFulfilled,
        (state, action) => {
          state.loading = false;

          state.trades = [];

          traderSlice.caseReducers.addUpdateTrades(state, action);
        },
      )
      .addMatcher(equityApi.endpoints.getLatestPrices.matchPending, state => {
        state.loading = true;
      })
      .addMatcher(equityApi.endpoints.getLatestPrices.matchRejected, state => {
        state.loading = false;
      })
      .addMatcher(proposalsApi.endpoints.createProposal.matchFulfilled, state => {
        state.trades = [];
      });
  },
});

export const {
  addUpdateTrades,
  deleteTrade,
  updateTradeAccounts,
  setTrades,
  resetTrades,
  setOpenTrades,
  resetOpenTrades,
} = traderSlice.actions;

export const traderReducer = traderSlice.reducer;
